import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PASSWORD_KEY = "alchemy_admin_password";

const fmt = (iso) => {
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
};

const Spinner = () => (
    <span
        aria-hidden
        style={{
            display: "inline-block",
            width: 12,
            height: 12,
            border: "2px solid #E2DDD8",
            borderTopColor: "#9B7D5A",
            borderRadius: "50%",
            animation: "spin 0.9s linear infinite",
            verticalAlign: "middle",
        }}
    />
);

const ConfirmDialog = ({ open, title, body, confirmLabel, onCancel, onConfirm, busy }) => {
    if (!open) return null;
    return (
        <div
            role="dialog"
            aria-modal="true"
            data-testid="comment-confirm-dialog"
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(28, 24, 22, 0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                padding: 20,
            }}
            onClick={onCancel}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: "#FAFAF8",
                    maxWidth: 460,
                    width: "100%",
                    padding: "36px 32px 28px",
                    border: "1px solid #E2DDD8",
                    boxShadow: "0 20px 60px -20px rgba(0,0,0,0.35)",
                }}
            >
                <h3
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: 26,
                        color: "#2C2825",
                        margin: 0,
                    }}
                >
                    {title}
                </h3>
                <p
                    style={{
                        fontFamily: "'Figtree', sans-serif",
                        fontSize: 14,
                        color: "#8A7968",
                        lineHeight: 1.65,
                        marginTop: 12,
                    }}
                >
                    {body}
                </p>
                <div style={{ marginTop: 28, display: "flex", gap: 14, justifyContent: "flex-end" }}>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        data-testid="comment-confirm-cancel"
                        style={{
                            fontFamily: "'Figtree', sans-serif",
                            fontSize: 11,
                            letterSpacing: "0.24em",
                            textTransform: "uppercase",
                            color: "#8A7968",
                            background: "transparent",
                            border: "1px solid #E2DDD8",
                            padding: "10px 18px",
                            cursor: "pointer",
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={busy}
                        data-testid="comment-confirm-ok"
                        style={{
                            fontFamily: "'Figtree', sans-serif",
                            fontSize: 11,
                            letterSpacing: "0.24em",
                            textTransform: "uppercase",
                            color: "#FAFAF8",
                            background: "#7D2F3F",
                            border: "1px solid #7D2F3F",
                            padding: "10px 18px",
                            cursor: busy ? "wait" : "pointer",
                        }}
                    >
                        {busy ? "Deleting…" : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ReplyForm = ({ onSubmit, onCancel, busy }) => {
    const [text, setText] = useState("");
    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                const trimmed = text.trim();
                if (!trimmed) return;
                onSubmit(trimmed, () => setText(""));
            }}
            data-testid="comment-reply-form"
            style={{ marginTop: 14 }}
        >
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                required
                placeholder="Write a reply as Austine…"
                data-testid="comment-reply-input"
                className="editorial-textarea"
                style={{ minHeight: 90 }}
            />
            <div style={{ marginTop: 10, display: "flex", gap: 12 }}>
                <button
                    type="submit"
                    disabled={busy}
                    data-testid="comment-reply-submit"
                    className="editorial-button-primary"
                    style={{ padding: "10px 22px", fontSize: 11 }}
                >
                    {busy ? "Posting…" : "Post Reply"}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    style={{
                        fontFamily: "'Figtree', sans-serif",
                        fontSize: 11,
                        letterSpacing: "0.24em",
                        textTransform: "uppercase",
                        color: "#8A7968",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                    }}
                >
                    Cancel
                </button>
            </div>
        </form>
    );
};

const CommentItem = ({ comment, replies, isAdmin, onReply, onDelete, replyingTo, setReplyingTo }) => {
    return (
        <li
            data-testid={`comment-item-${comment.id}`}
            style={{
                listStyle: "none",
                padding: "26px 26px",
                background: "#FAFAF8",
                border: "1px solid #E2DDD8",
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
                <p
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: 20,
                        color: "#2C2825",
                        margin: 0,
                        fontStyle: "italic",
                    }}
                >
                    {comment.name}
                </p>
                <time
                    style={{
                        fontFamily: "'Figtree', sans-serif",
                        fontSize: 11,
                        letterSpacing: "0.14em",
                        color: "#8A7968",
                        textTransform: "uppercase",
                    }}
                    dateTime={comment.created_at}
                >
                    {fmt(comment.created_at)}
                </time>
            </div>
            <p
                style={{
                    fontFamily: "'Figtree', sans-serif",
                    fontSize: 15,
                    lineHeight: 1.8,
                    color: "#2C2825",
                    marginTop: 14,
                    whiteSpace: "pre-wrap",
                }}
            >
                {comment.message}
            </p>

            {isAdmin && (
                <div style={{ marginTop: 14, display: "flex", gap: 18 }}>
                    <button
                        type="button"
                        onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                        data-testid={`comment-reply-toggle-${comment.id}`}
                        style={{
                            fontFamily: "'Figtree', sans-serif",
                            fontSize: 11,
                            letterSpacing: "0.22em",
                            textTransform: "uppercase",
                            color: "#9B7D5A",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                        }}
                    >
                        {replyingTo === comment.id ? "Close" : "Reply as Author"}
                    </button>
                    <button
                        type="button"
                        onClick={() => onDelete(comment)}
                        data-testid={`comment-delete-${comment.id}`}
                        style={{
                            fontFamily: "'Figtree', sans-serif",
                            fontSize: 11,
                            letterSpacing: "0.22em",
                            textTransform: "uppercase",
                            color: "#997777",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                        }}
                    >
                        Delete
                    </button>
                </div>
            )}

            {isAdmin && replyingTo === comment.id && (
                <ReplyForm
                    onSubmit={(msg, reset) => onReply(comment.id, msg, reset)}
                    onCancel={() => setReplyingTo(null)}
                />
            )}

            {replies.length > 0 && (
                <ul
                    style={{
                        marginTop: 22,
                        marginLeft: 0,
                        padding: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 14,
                    }}
                >
                    {replies.map((r) => (
                        <li
                            key={r.id}
                            data-testid={`comment-reply-${r.id}`}
                            style={{
                                listStyle: "none",
                                padding: "20px 22px",
                                background: "#F2EDE6",
                                borderLeft: "3px solid #9B7D5A",
                                marginLeft: 16,
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "baseline",
                                    flexWrap: "wrap",
                                    gap: 8,
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <p
                                        style={{
                                            fontFamily: "'Cormorant Garamond', serif",
                                            fontSize: 18,
                                            color: "#2C2825",
                                            margin: 0,
                                            fontStyle: "italic",
                                        }}
                                    >
                                        {r.name}
                                    </p>
                                    <span
                                        data-testid={`comment-author-badge-${r.id}`}
                                        style={{
                                            display: "inline-block",
                                            fontFamily: "'Figtree', sans-serif",
                                            fontSize: 9,
                                            letterSpacing: "0.24em",
                                            textTransform: "uppercase",
                                            color: "#FAFAF8",
                                            background: "#9B7D5A",
                                            padding: "3px 8px",
                                        }}
                                    >
                                        Author
                                    </span>
                                </div>
                                <time
                                    style={{
                                        fontFamily: "'Figtree', sans-serif",
                                        fontSize: 10,
                                        letterSpacing: "0.14em",
                                        color: "#8A7968",
                                        textTransform: "uppercase",
                                    }}
                                    dateTime={r.created_at}
                                >
                                    {fmt(r.created_at)}
                                </time>
                            </div>
                            <p
                                style={{
                                    fontFamily: "'Figtree', sans-serif",
                                    fontSize: 14,
                                    lineHeight: 1.8,
                                    color: "#2C2825",
                                    marginTop: 10,
                                    whiteSpace: "pre-wrap",
                                }}
                            >
                                {r.message}
                            </p>
                            {isAdmin && (
                                <div style={{ marginTop: 10 }}>
                                    <button
                                        type="button"
                                        onClick={() => onDelete(r)}
                                        data-testid={`comment-delete-${r.id}`}
                                        style={{
                                            fontFamily: "'Figtree', sans-serif",
                                            fontSize: 10,
                                            letterSpacing: "0.22em",
                                            textTransform: "uppercase",
                                            color: "#997777",
                                            background: "transparent",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: 0,
                                        }}
                                    >
                                        Delete reply
                                    </button>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
};

export default function Comments({ postSlug }) {
    const [data, setData] = useState(null); // { enabled, disabled_message, comments }
    const [error, setError] = useState(null);
    const [form, setForm] = useState({ name: "", message: "", website: "" });
    const [posting, setPosting] = useState(false);
    const [replyingTo, setReplyingTo] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const adminPassword = typeof window !== "undefined" ? sessionStorage.getItem(PASSWORD_KEY) : null;
    const isAdmin = !!adminPassword;

    const fetchComments = useCallback(async () => {
        try {
            const headers = adminPassword ? { "x-admin-password": adminPassword } : {};
            const r = await axios.get(`${API}/blog/${postSlug}/comments`, { headers });
            setData(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not load comments.");
        }
    }, [postSlug, adminPassword]);

    useEffect(() => {
        if (!postSlug) return;
        fetchComments();
    }, [postSlug, fetchComments]);

    const { topLevel, repliesBy } = useMemo(() => {
        const all = data?.comments || [];
        const top = all.filter((c) => !c.parent_id);
        const replies = {};
        all.filter((c) => c.parent_id).forEach((r) => {
            replies[r.parent_id] = [...(replies[r.parent_id] || []), r];
        });
        Object.values(replies).forEach((arr) =>
            arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        );
        top.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        return { topLevel: top, repliesBy: replies };
    }, [data]);

    const submitComment = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.message.trim()) {
            toast.error("Name and reflection are required.");
            return;
        }
        setPosting(true);
        try {
            await axios.post(`${API}/blog/${postSlug}/comments`, {
                name: form.name.trim(),
                message: form.message.trim(),
                website: form.website,
            });
            setForm({ name: "", message: "", website: "" });
            toast.success("Thank you — your reflection has been shared.");
            await fetchComments();
        } catch (err) {
            const status = err?.response?.status;
            if (status === 429) {
                toast.error(err.response.data.detail);
            } else if (status === 403) {
                toast.error("Comments are currently closed.");
                await fetchComments();
            } else {
                toast.error(err?.response?.data?.detail || "Could not post your comment.");
            }
        } finally {
            setPosting(false);
        }
    };

    const submitReply = async (commentId, message, resetField) => {
        try {
            await axios.post(
                `${API}/blog/${postSlug}/comments/${commentId}/reply`,
                { message },
                { headers: { "x-admin-password": adminPassword } }
            );
            resetField();
            setReplyingTo(null);
            toast.success("Reply posted.");
            await fetchComments();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not post reply.");
        }
    };

    const requestDelete = (target) => setPendingDelete(target);

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        try {
            await axios.delete(`${API}/comments/${pendingDelete.id}`, {
                headers: { "x-admin-password": adminPassword },
            });
            toast.success("Deleted.");
            setPendingDelete(null);
            await fetchComments();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not delete.");
        } finally {
            setDeleting(false);
        }
    };

    if (error) {
        return (
            <section
                data-testid="comments-section"
                style={{ background: "#FAFAF8", padding: "60px 0", borderTop: "1px solid #E2DDD8" }}
            >
                <div className="mx-auto" style={{ maxWidth: 720, padding: "0 24px" }}>
                    <p style={{ color: "#997777", fontFamily: "'Figtree', sans-serif" }}>{error}</p>
                </div>
            </section>
        );
    }

    if (!data) {
        return (
            <section
                data-testid="comments-section"
                style={{ background: "#FAFAF8", padding: "60px 0", borderTop: "1px solid #E2DDD8" }}
            >
                <div className="mx-auto" style={{ maxWidth: 720, padding: "0 24px", textAlign: "center" }}>
                    <Spinner />
                    <span
                        style={{
                            marginLeft: 10,
                            fontFamily: "'Figtree', sans-serif",
                            color: "#8A7968",
                            fontSize: 13,
                        }}
                    >
                        Loading reflections…
                    </span>
                </div>
            </section>
        );
    }

    // Visitor view when off
    if (!data.enabled && !isAdmin) {
        return (
            <section
                data-testid="comments-section"
                data-comments-disabled="true"
                style={{
                    background: "#F2EDE6",
                    padding: "72px 0",
                    borderTop: "1px solid #E2DDD8",
                }}
            >
                <div className="mx-auto" style={{ maxWidth: 720, padding: "0 24px", textAlign: "center" }}>
                    <span
                        aria-hidden
                        style={{ fontFamily: "'Cormorant Garamond', serif", color: "#9B7D5A", fontSize: 22 }}
                    >
                        ✦
                    </span>
                    <p
                        data-testid="comments-disabled-message"
                        style={{
                            marginTop: 16,
                            fontFamily: "'Cormorant Garamond', serif",
                            fontStyle: "italic",
                            color: "#2C2825",
                            fontSize: 22,
                            lineHeight: 1.55,
                        }}
                    >
                        {data.disabled_message}
                    </p>
                </div>
            </section>
        );
    }

    return (
        <section
            data-testid="comments-section"
            style={{
                background: "#F2EDE6",
                padding: "80px 0 100px",
                borderTop: "1px solid #E2DDD8",
            }}
        >
            <div className="mx-auto" style={{ maxWidth: 760, padding: "0 24px" }}>
                <div style={{ textAlign: "center" }}>
                    <p
                        style={{
                            fontFamily: "'Figtree', sans-serif",
                            fontSize: 10,
                            letterSpacing: "0.32em",
                            textTransform: "uppercase",
                            color: "#9B7D5A",
                        }}
                    >
                        Reflections
                    </p>
                    <h2
                        style={{
                            marginTop: 14,
                            fontFamily: "'Cormorant Garamond', serif",
                            fontSize: "clamp(28px, 3vw, 38px)",
                            color: "#2C2825",
                            lineHeight: 1.15,
                        }}
                    >
                        {topLevel.length === 0
                            ? "Be the first to leave a reflection."
                            : "The conversation so far."}
                    </h2>
                    <div
                        style={{
                            margin: "22px auto 0",
                            width: 48,
                            height: 1,
                            background: "#9B7D5A",
                            opacity: 0.7,
                        }}
                    />
                </div>

                {isAdmin && !data.enabled && (
                    <p
                        data-testid="comments-admin-paused-banner"
                        style={{
                            marginTop: 28,
                            padding: "12px 18px",
                            background: "#FAFAF8",
                            border: "1px dashed #9B7D5A",
                            fontFamily: "'Figtree', sans-serif",
                            fontSize: 12,
                            color: "#8A7968",
                            textAlign: "center",
                        }}
                    >
                        Comments are paused for visitors. You are viewing the moderation view.
                    </p>
                )}

                {topLevel.length > 0 && (
                    <ul
                        data-testid="comments-list"
                        style={{
                            marginTop: 44,
                            padding: 0,
                            display: "flex",
                            flexDirection: "column",
                            gap: 22,
                        }}
                    >
                        {topLevel.map((c) => (
                            <CommentItem
                                key={c.id}
                                comment={c}
                                replies={repliesBy[c.id] || []}
                                isAdmin={isAdmin}
                                onReply={submitReply}
                                onDelete={requestDelete}
                                replyingTo={replyingTo}
                                setReplyingTo={setReplyingTo}
                            />
                        ))}
                    </ul>
                )}

                {data.enabled ? (
                    <form
                        onSubmit={submitComment}
                        data-testid="comment-form"
                        style={{
                            marginTop: topLevel.length > 0 ? 56 : 36,
                            background: "#FAFAF8",
                            padding: "36px 32px",
                            border: "1px solid #E2DDD8",
                        }}
                    >
                        <p
                            style={{
                                fontFamily: "'Figtree', sans-serif",
                                fontSize: 10,
                                letterSpacing: "0.3em",
                                textTransform: "uppercase",
                                color: "#9B7D5A",
                            }}
                        >
                            Share a reflection
                        </p>
                        <h3
                            style={{
                                marginTop: 8,
                                fontFamily: "'Cormorant Garamond', serif",
                                fontSize: 26,
                                color: "#2C2825",
                            }}
                        >
                            Leave a comment
                        </h3>

                        <div style={{ marginTop: 22 }}>
                            <label
                                htmlFor="comment-name"
                                style={{
                                    fontFamily: "'Figtree', sans-serif",
                                    fontSize: 11,
                                    letterSpacing: "0.22em",
                                    textTransform: "uppercase",
                                    color: "#8A7968",
                                    display: "block",
                                    marginBottom: 6,
                                }}
                            >
                                Name
                            </label>
                            <input
                                id="comment-name"
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                maxLength={120}
                                required
                                data-testid="comment-name-input"
                                className="editorial-input"
                            />
                        </div>

                        <div style={{ marginTop: 18 }}>
                            <label
                                htmlFor="comment-message"
                                style={{
                                    fontFamily: "'Figtree', sans-serif",
                                    fontSize: 11,
                                    letterSpacing: "0.22em",
                                    textTransform: "uppercase",
                                    color: "#8A7968",
                                    display: "block",
                                    marginBottom: 6,
                                }}
                            >
                                Your reflection
                            </label>
                            <textarea
                                id="comment-message"
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                rows={5}
                                required
                                maxLength={4000}
                                data-testid="comment-message-input"
                                className="editorial-textarea"
                                style={{ minHeight: 120 }}
                            />
                        </div>

                        {/* honeypot — visually hidden, not aria-hidden so screen readers ignore via tabindex */}
                        <div
                            aria-hidden
                            style={{
                                position: "absolute",
                                left: "-9999px",
                                top: "auto",
                                width: 1,
                                height: 1,
                                overflow: "hidden",
                            }}
                        >
                            <label htmlFor="comment-website">Website (leave empty)</label>
                            <input
                                id="comment-website"
                                type="text"
                                tabIndex={-1}
                                autoComplete="off"
                                value={form.website}
                                onChange={(e) => setForm({ ...form, website: e.target.value })}
                                data-testid="comment-honeypot"
                            />
                        </div>

                        <div style={{ marginTop: 26 }}>
                            <button
                                type="submit"
                                disabled={posting}
                                data-testid="comment-submit"
                                className="editorial-button-primary"
                                style={{ padding: "14px 28px" }}
                            >
                                {posting ? "Posting…" : "Share Reflection"}
                            </button>
                        </div>
                    </form>
                ) : null}
            </div>

            <ConfirmDialog
                open={!!pendingDelete}
                title="Delete this comment?"
                body={
                    pendingDelete
                        ? pendingDelete.parent_id
                            ? "This reply will be removed permanently."
                            : "This comment and all of its replies will be removed permanently."
                        : ""
                }
                confirmLabel="Delete"
                onCancel={() => setPendingDelete(null)}
                onConfirm={confirmDelete}
                busy={deleting}
            />

            <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
        </section>
    );
}
